import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import {
    getKitchenOrders,
    setFoodOrderStatus,
    setFoodOrderSchedule,
    setFoodOrderUnitStatus,
} from "../../../api/foodApi";

import type {
    FoodOrder,
    FoodOrderStatus,
} from "./foodTypes";

import {
    estimateOrderMinutes,
    formatEstimatedTime,
    getEstimatedReadyAt,
    getOrderBoxCount,
    isFuturePreOrder,
} from "./foodEstimate";

import "./playerLoungeFood.css";

const statusText: Record<FoodOrderStatus, string> = {
    Received: "Ny ordre",
    InProgress: "I gang",
    Ready: "Klar til afhentning",
    Delivered: "Udleveret",
    Cancelled: "Annulleret",
};

function normalizeStatus(status: any): FoodOrderStatus {
    if (
        status === "Received" ||
        status === "InProgress" ||
        status === "Ready" ||
        status === "Delivered" ||
        status === "Cancelled"
    ) {
        return status;
    }

    switch (Number(status)) {
        case 0:
            return "Received";
        case 1:
            return "InProgress";
        case 2:
            return "Ready";
        case 3:
            return "Delivered";
        case 4:
            return "Cancelled";
        default:
            return "Received";
    }
}

function nextStatus(
    status: FoodOrderStatus
): FoodOrderStatus | null {
    if (status === "Received")
        return "InProgress";

    if (status === "InProgress")
        return "Ready";

    if (status === "Ready")
        return "Delivered";

    return null;
}

export default function FoodOrdersPage() {
    const { eventId } = useParams();

    const parsedEventId =
        Number(eventId);

    const [orders, setOrders] =
        useState<FoodOrder[]>([]);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [search, setSearch] =
        useState("");

    const [statusFilter, setStatusFilter] =
        useState<
            "All" |
            "PreOrder" |
            FoodOrderStatus
        >("All");

    const [updatingId, setUpdatingId] =
        useState<number | null>(null);

    const [updatingUnitId, setUpdatingUnitId] =
        useState<number | null>(null);

    const [clockTick, setClockTick] =
        useState(0);

    const [microwaveAssignments] =
        useState<Record<number, { oven: string; startedAt: string }>>(
            () => {
                try {
                    const saved =
                        window.localStorage.getItem(
                            `playerLoungeMicrowave-${eventId ?? "unknown"}`
                        );

                    return saved
                        ? JSON.parse(saved)
                        : {};
                } catch {
                    return {};
                }
            }
        );

    useEffect(() => {
        const timer =
            window.setInterval(
                () =>
                    setClockTick(
                        value => value + 1
                    ),
                1000
            );

        return () =>
            window.clearInterval(timer);
    }, []);

    const loadOrders =
        useCallback(async () => {
            if (!parsedEventId)
                return;

            try {
                const data =
                    await getKitchenOrders(
                        parsedEventId
                    );

                setOrders(
                    data.map(order => ({
                        ...order,
                        status:
                            normalizeStatus(
                                order.status
                            ),
                        units:
                            (order.units ?? []).map(
                                unit => ({
                                    ...unit,
                                    status:
                                        normalizeStatus(
                                            unit.status
                                        ),
                                })
                            ),
                    }))
                );

                setError("");
            } catch (err) {
                console.error(err);

                setError(
                    err instanceof Error
                        ? err.message
                        : "Kunne ikke hente ordrer."
                );
            } finally {
                setLoading(false);
            }
        }, [parsedEventId]);

    useEffect(() => {
        loadOrders();

        const timer =
            window.setInterval(
                loadOrders,
                5000
            );

        return () =>
            window.clearInterval(
                timer
            );
    }, [loadOrders]);

    const activeOrders =
        useMemo(() => {
            const q =
                search
                    .trim()
                    .toLowerCase();

            return orders
                .filter(order => {
                    const status =
                        normalizeStatus(
                            order.status
                        );

                    // Historik hører ikke hjemme her
                    if (
                        status === "Delivered" ||
                        status === "Cancelled"
                    ) {
                        return false;
                    }

                    const preOrder =
                        status === "Received" &&
                        isFuturePreOrder(
                            order,
                            new Date()
                        );

                    if (
                        statusFilter === "PreOrder" &&
                        !preOrder
                    ) {
                        return false;
                    }

                    if (
                        statusFilter !== "All" &&
                        statusFilter !== "PreOrder" &&
                        status !== statusFilter
                    ) {
                        return false;
                    }

                    // "Nye" skal kun vise de ordrer,
                    // der faktisk er frigivet til køkkenet.
                    if (
                        statusFilter === "Received" &&
                        preOrder
                    ) {
                        return false;
                    }

                    if (!q)
                        return true;

                    const searchText = [
                        order.id,
                        order.personId,
                        order.personName,
                        order.note,
                        order.scheduledFor,
                        ...order.lines.map(
                            x => x.name
                        ),
                        ...(order.units ?? []).map(
                            x => x.name
                        ),
                    ]
                        .filter(Boolean)
                        .join(" ")
                        .toLowerCase();

                    return searchText.includes(q);
                })
                .sort(
                    (a, b) => {
                        const now =
                            new Date();

                        const aPre =
                            normalizeStatus(
                                a.status
                            ) === "Received" &&
                            isFuturePreOrder(
                                a,
                                now
                            );

                        const bPre =
                            normalizeStatus(
                                b.status
                            ) === "Received" &&
                            isFuturePreOrder(
                                b,
                                now
                            );

                        if (aPre && bPre) {
                            return (
                                new Date(
                                    a.scheduledFor ??
                                    a.createdAt
                                ).getTime() -
                                new Date(
                                    b.scheduledFor ??
                                    b.createdAt
                                ).getTime()
                            );
                        }

                        if (aPre !== bPre) {
                            return aPre ? -1 : 1;
                        }

                        const priority = (
                            order: FoodOrder
                        ) => {
                            const status =
                                normalizeStatus(
                                    order.status
                                );

                            if (
                                microwaveAssignments[
                                order.id
                                ]
                            ) return 0;

                            if (
                                status ===
                                "InProgress"
                            ) return 1;

                            if (
                                status ===
                                "Received"
                            ) return 2;

                            if (
                                status ===
                                "Ready"
                            ) return 3;

                            return 99;
                        };

                        const pA =
                            priority(a);

                        const pB =
                            priority(b);

                        if (pA !== pB)
                            return pA - pB;

                        return (
                            new Date(
                                a.createdAt
                            ).getTime() -
                            new Date(
                                b.createdAt
                            ).getTime()
                        );
                    }
                );
        }, [
            orders,
            search,
            statusFilter,
            clockTick,
            microwaveAssignments,
        ]);

    async function advance(
        order: FoodOrder
    ) {
        const status =
            normalizeStatus(
                order.status
            );

        const next =
            nextStatus(status);

        if (!next)
            return;

        setUpdatingId(order.id);

        try {
            const updated =
                await setFoodOrderStatus(
                    parsedEventId,
                    order.id,
                    next
                );

            setOrders(prev =>
                prev.map(x =>
                    x.id === order.id
                        ? {
                            ...updated,
                            status:
                                normalizeStatus(
                                    updated.status
                                ),
                        }
                        : x
                )
            );
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke ændre status."
            );
        } finally {
            setUpdatingId(null);
        }
    }


    async function releasePreOrder(
        order: FoodOrder
    ) {
        setUpdatingId(order.id);

        try {
            const updated =
                await setFoodOrderSchedule(
                    parsedEventId,
                    order.id,
                    null
                );

            setOrders(prev =>
                prev.map(x =>
                    x.id === order.id
                        ? {
                            ...updated,
                            status:
                                normalizeStatus(
                                    updated.status
                                ),
                        }
                        : x
                )
            );
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke frigive forudbestillingen."
            );
        } finally {
            setUpdatingId(null);
        }
    }

    async function cancelOrder(order: FoodOrder) {
        if (
            !window.confirm(
                `Vil du annullere ordre #${order.id}?`
            )
        ) {
            return;
        }

        setUpdatingId(order.id);

        try {
            const updated =
                await setFoodOrderStatus(
                    parsedEventId,
                    order.id,
                    "Cancelled"
                );

            setOrders(prev =>
                prev.map(x =>
                    x.id === order.id
                        ? {
                            ...updated,
                            status:
                                normalizeStatus(
                                    updated.status
                                ),
                        }
                        : x
                )
            );
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke annullere ordren."
            );
        } finally {
            setUpdatingId(null);
        }
    }
    async function changeUnitStatus(
        order: FoodOrder,
        unitId: number,
        status: FoodOrderStatus
    ) {
        setUpdatingUnitId(unitId);
        setError("");

        try {
            const updated =
                await setFoodOrderUnitStatus(
                    parsedEventId,
                    order.id,
                    unitId,
                    status
                );

            setOrders(prev =>
                prev.map(x =>
                    x.id === order.id
                        ? {
                            ...updated,
                            status:
                                normalizeStatus(
                                    updated.status
                                ),
                            units:
                                (updated.units ?? []).map(
                                    unit => ({
                                        ...unit,
                                        status:
                                            normalizeStatus(
                                                unit.status
                                            ),
                                    })
                                ),
                        }
                        : x
                )
            );
        } catch (err) {
            setError(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke ændre status på boksen."
            );
        } finally {
            setUpdatingUnitId(null);
        }
    }

    function getUnitStatusText(
        status: FoodOrderStatus
    ) {
        if (status === "Received")
            return "Ny";

        if (status === "InProgress")
            return "I gang";

        if (status === "Ready")
            return "Klar";

        if (status === "Delivered")
            return "Udleveret";

        if (status === "Cancelled")
            return "Annulleret";

        return status;
    }

    function getButtonText(
        status: FoodOrderStatus
    ) {
        if (status === "Received")
            return "Sæt i gang";

        if (status === "InProgress")
            return "Marker som klar";

        if (status === "Ready")
            return "Marker udleveret";

        return "";
    }

    return (
        <>
            <header className="pl-topbar">
                <div>
                    <h1>Ordrer</h1>

                    <div className="pl-steps">
                        Se og administrér aktive
                        Player Lounge-ordrer
                    </div>
                </div>

                <div className="pl-live-indicator">
                    ● Live
                </div>
            </header>

            <div className="pl-content">
                <div className="pl-orders-toolbar">
                    <div className="pl-order-filters">
                        <button
                            className={
                                statusFilter === "All"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setStatusFilter("All")
                            }
                        >
                            Alle
                        </button>

                        <button
                            className={
                                statusFilter === "PreOrder"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setStatusFilter(
                                    "PreOrder"
                                )
                            }
                        >
                            Forudbestilling
                        </button>

                        <button
                            className={
                                statusFilter === "Received"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setStatusFilter(
                                    "Received"
                                )
                            }
                        >
                            Nye
                        </button>

                        <button
                            className={
                                statusFilter === "InProgress"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setStatusFilter(
                                    "InProgress"
                                )
                            }
                        >
                            I gang
                        </button>

                        <button
                            className={
                                statusFilter === "Ready"
                                    ? "active"
                                    : ""
                            }
                            onClick={() =>
                                setStatusFilter(
                                    "Ready"
                                )
                            }
                        >
                            Klar
                        </button>
                    </div>

                    <input
                        className="pl-search"
                        placeholder="Søg ordre, person eller vare..."
                        value={search}
                        onChange={e =>
                            setSearch(
                                e.target.value
                            )
                        }
                    />
                </div>

                {error && (
                    <div className="pl-message">
                        {error}
                    </div>
                )}

                {loading ? (
                    <div className="pl-page-loading">
                        Henter ordrer...
                    </div>
                ) : activeOrders.length === 0 ? (
                    <div className="pl-empty-state">
                        <strong>
                            Ingen aktive ordrer
                        </strong>

                        <span>
                            Nye ordrer vises her
                            automatisk.
                        </span>
                    </div>
                ) : (
                    <div className="pl-orders-list">
                        {activeOrders.map(
                            order => {
                                const status =
                                    normalizeStatus(
                                        order.status
                                    );

                                const preOrder =
                                    status === "Received" &&
                                    isFuturePreOrder(
                                        order,
                                        new Date()
                                    );

                                const estimatedMinutes =
                                    estimateOrderMinutes(
                                        order,
                                        orders,
                                        new Date(),
                                        {
                                            microwaveOrderIds:
                                                Object.keys(
                                                    microwaveAssignments
                                                ).map(Number),
                                        }
                                    );

                                const estimatedReadyAt =
                                    getEstimatedReadyAt(
                                        estimatedMinutes
                                    );

                                const boxCount =
                                    getOrderBoxCount(
                                        order
                                    );

                                const units =
                                    order.units ?? [];

                                const hasUnitWorkflow =
                                    order.splitOrder &&
                                    units.length > 0;

                                const readyUnitCount =
                                    units.filter(
                                        unit =>
                                            normalizeStatus(
                                                unit.status
                                            ) === "Ready"
                                    ).length;

                                const inProgressUnitCount =
                                    units.filter(
                                        unit =>
                                            normalizeStatus(
                                                unit.status
                                            ) === "InProgress"
                                    ).length;

                                const microwave =
                                    microwaveAssignments[
                                    order.id
                                    ];

                                return (
                                    <article
                                        className="pl-order-list-card"
                                        key={
                                            order.id
                                        }
                                    >
                                        <div className="pl-order-list-main">
                                            <div className="pl-order-number">
                                                <strong>
                                                    #
                                                    {
                                                        order.id
                                                    }
                                                </strong>

                                                <span>
                                                    {new Date(
                                                        order.createdAt
                                                    ).toLocaleTimeString(
                                                        "da-DK",
                                                        {
                                                            hour: "2-digit",
                                                            minute: "2-digit",
                                                        }
                                                    )}
                                                </span>
                                            </div>

                                            <div className="pl-order-person">
                                                <strong>
                                                    {order.personName ||
                                                        `Person #${order.personId}`}
                                                </strong>

                                                <span>
                                                    Person ID{" "}
                                                    {
                                                        order.personId
                                                    }
                                                </span>
                                            </div>

                                            <div className="pl-order-products">
                                                {order.lines.map(
                                                    (
                                                        line,
                                                        index
                                                    ) => (
                                                        <span
                                                            key={`${line.menuItemId}-${index}`}
                                                        >
                                                            {line.quantity >
                                                                1
                                                                ? `${line.quantity} × `
                                                                : ""}

                                                            {
                                                                line.name
                                                            }
                                                        </span>
                                                    )
                                                )}
                                            </div>

                                            <div>
                                                <span
                                                    className={`pl-order-status status-${status}`}
                                                >
                                                    {preOrder
                                                        ? "Forudbestilling"
                                                        : microwave
                                                            ? `Mikroovn ${microwave.oven}`
                                                            : statusText[
                                                            status
                                                            ]}
                                                </span>
                                            </div>

                                            <div className="pl-order-price">
                                                <strong>
                                                    {order.total.toFixed(
                                                        0
                                                    )}{" "}
                                                    kr.
                                                </strong>
                                            </div>
                                        </div>

                                        {preOrder &&
                                            order.scheduledFor && (
                                                <div
                                                    style={{
                                                        marginTop: "10px",
                                                        padding: "10px 12px",
                                                        borderRadius: "8px",
                                                        background:
                                                            "rgba(65,105,225,0.10)",
                                                    }}
                                                >
                                                    <strong>
                                                        🕒 Forudbestilt til{" "}
                                                        {new Date(
                                                            order.scheduledFor
                                                        ).toLocaleString(
                                                            "da-DK",
                                                            {
                                                                day: "2-digit",
                                                                month: "2-digit",
                                                                hour: "2-digit",
                                                                minute: "2-digit",
                                                            }
                                                        )}
                                                    </strong>
                                                </div>
                                            )}

                                        {!preOrder &&
                                            estimatedMinutes > 0 && (
                                                <div
                                                    style={{
                                                        marginTop: "10px",
                                                        padding: "10px 12px",
                                                        borderRadius: "8px",
                                                        background:
                                                            "rgba(65,105,225,0.10)",
                                                    }}
                                                >
                                                    <strong>
                                                        ⏱ Ca. klar kl.{" "}
                                                        {formatEstimatedTime(
                                                            estimatedReadyAt
                                                        )}
                                                    </strong>

                                                    <span
                                                        style={{
                                                            display: "block",
                                                            marginTop: "3px",
                                                            fontSize: "12px",
                                                            opacity: 0.8,
                                                        }}
                                                    >
                                                        Ca. {estimatedMinutes} min.
                                                        {" · "}
                                                        {boxCount}{" "}
                                                        {boxCount === 1
                                                            ? "boks"
                                                            : "bokse"}
                                                    </span>
                                                </div>
                                            )}

                                        {hasUnitWorkflow && (
                                            <div
                                                style={{
                                                    marginTop: "12px",
                                                    padding: "12px",
                                                    borderRadius: "10px",
                                                    border:
                                                        "1px solid rgba(0,0,0,0.12)",
                                                    display: "grid",
                                                    gap: "8px",
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        gap: "10px",
                                                        alignItems: "center",
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    <strong>
                                                        🏷️ Splitordre · {readyUnitCount}/{units.length} klar
                                                    </strong>

                                                    <small>
                                                        {inProgressUnitCount > 0
                                                            ? `${inProgressUnitCount} i gang`
                                                            : "Ingen bokse i gang"}
                                                    </small>
                                                </div>

                                                {units
                                                    .slice()
                                                    .sort(
                                                        (a, b) =>
                                                            a.unitNumber -
                                                            b.unitNumber
                                                    )
                                                    .map(unit => {
                                                        const unitStatus =
                                                            normalizeStatus(
                                                                unit.status
                                                            );

                                                        return (
                                                            <div
                                                                key={
                                                                    unit.id
                                                                }
                                                                style={{
                                                                    display:
                                                                        "grid",
                                                                    gridTemplateColumns:
                                                                        "minmax(0, 1fr) auto",
                                                                    gap: "10px",
                                                                    alignItems:
                                                                        "center",
                                                                    padding:
                                                                        "9px 10px",
                                                                    borderRadius:
                                                                        "8px",
                                                                    background:
                                                                        "rgba(65,105,225,0.06)",
                                                                }}
                                                            >
                                                                <div>
                                                                    <strong>
                                                                        {unit.unitNumber}/{unit.unitCount}
                                                                        {" · "}
                                                                        {unit.name}
                                                                    </strong>

                                                                    <small
                                                                        style={{
                                                                            display:
                                                                                "block",
                                                                            marginTop:
                                                                                "2px",
                                                                            opacity:
                                                                                0.75,
                                                                        }}
                                                                    >
                                                                        {getUnitStatusText(
                                                                            unitStatus
                                                                        )}
                                                                    </small>
                                                                </div>

                                                                <div
                                                                    style={{
                                                                        display:
                                                                            "flex",
                                                                        gap: "6px",
                                                                        flexWrap:
                                                                            "wrap",
                                                                        justifyContent:
                                                                            "flex-end",
                                                                    }}
                                                                >
                                                                    {unitStatus ===
                                                                        "Received" && (
                                                                            <button
                                                                                type="button"
                                                                                disabled={
                                                                                    updatingUnitId ===
                                                                                    unit.id
                                                                                }
                                                                                onClick={() =>
                                                                                    changeUnitStatus(
                                                                                        order,
                                                                                        unit.id,
                                                                                        "InProgress"
                                                                                    )
                                                                                }
                                                                            >
                                                                                {updatingUnitId ===
                                                                                    unit.id
                                                                                    ? "Gemmer..."
                                                                                    : "Sæt i gang"}
                                                                            </button>
                                                                        )}

                                                                    {unitStatus ===
                                                                        "InProgress" && (
                                                                            <button
                                                                                type="button"
                                                                                disabled={
                                                                                    updatingUnitId ===
                                                                                    unit.id
                                                                                }
                                                                                onClick={() =>
                                                                                    changeUnitStatus(
                                                                                        order,
                                                                                        unit.id,
                                                                                        "Ready"
                                                                                    )
                                                                                }
                                                                            >
                                                                                {updatingUnitId ===
                                                                                    unit.id
                                                                                    ? "Gemmer..."
                                                                                    : "Klar"}
                                                                            </button>
                                                                        )}

                                                                    {unitStatus ===
                                                                        "Ready" && (
                                                                            <button
                                                                                type="button"
                                                                                disabled={
                                                                                    updatingUnitId ===
                                                                                    unit.id
                                                                                }
                                                                                onClick={() =>
                                                                                    changeUnitStatus(
                                                                                        order,
                                                                                        unit.id,
                                                                                        "InProgress"
                                                                                    )
                                                                                }
                                                                            >
                                                                                Tilbage
                                                                            </button>
                                                                        )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        )}

                                        {order.note && (
                                            <div className="pl-order-list-note">
                                                <strong>
                                                    Bemærkning:
                                                </strong>{" "}
                                                {
                                                    order.note
                                                }
                                            </div>
                                        )}

                                        <div className="pl-order-list-actions">
                                            {preOrder ? (
                                                <button
                                                    type="button"
                                                    disabled={
                                                        updatingId ===
                                                        order.id
                                                    }
                                                    onClick={() =>
                                                        releasePreOrder(
                                                            order
                                                        )
                                                    }
                                                >
                                                    {updatingId ===
                                                        order.id
                                                        ? "Flytter..."
                                                        : "Lav nu"}
                                                </button>
                                            ) : hasUnitWorkflow &&
                                                status !== "Ready" ? (
                                                <span
                                                    style={{
                                                        fontSize: "13px",
                                                        fontWeight: 700,
                                                        opacity: 0.75,
                                                    }}
                                                >
                                                    Status styres pr. boks
                                                </span>
                                            ) : (
                                                <button
                                                    disabled={
                                                        updatingId ===
                                                        order.id
                                                    }
                                                    onClick={() =>
                                                        advance(
                                                            order
                                                        )
                                                    }
                                                >
                                                    {updatingId ===
                                                        order.id
                                                        ? "Gemmer..."
                                                        : getButtonText(
                                                            status
                                                        )}
                                                </button>
                                            )}

                                            <button
                                                type="button"
                                                className="pl-cancel-order-button"
                                                disabled={updatingId === order.id}
                                                onClick={() => cancelOrder(order)}
                                            >
                                                Annuller
                                            </button>
                                        </div>
                                    </article>
                                );
                            }
                        )}
                    </div>
                )}
            </div>
        </>
    );
}